import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { Publico } from "../../../../../../../types/api/financeiro/datalhar-titulos-receber";
import { useState } from "react";
import SelectLocaisCobranca from "../../../../../../../components/SelectsSimpleApiQuery/SelectLocaisCobranca";
import PaperDraggable from "../../../../../../../components/PaperDraggable";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { TLocalCobranca } from "../../../../../../../types/api/financeiro/pesquisar-locais-cobranca";
import SelectPublico from "../../../../../../../components/SelectsSimpleApiQuery/SelectPublico";
import { IDetalharTituloPagar } from "../../../../../../../types/api/financeiro/detalhar-titulo-pagar";
import SelectTipoDocPagar from "../../../../../../../components/SelectsSimpleApiQuery/SelectTipoDocPagar";
import SelectDepartamentoFinanceiro from "../../../../../../../components/SelectsSimpleApiQuery/SelectDepartamentoFinanceiro";
import { IDataDepartamentosFinanceiros } from "../../../../../../../types/api/financeiro/pesquisar-departamentos-financeiros";

export default function DialogEdit({
    open,
    handleClose,
    titulo,
    codigo
}: DialogEditProps) {

    const queryClient = useQueryClient();

    const [internFilters, setInternFilters] = useState<TInternFilters>({
        publico: titulo.publico,
        tipo: titulo.tipo ? { id: titulo.tipo.id, nome: titulo.tipo.nome } : null,
        departamento: titulo.departamento,
        local_cobranca: titulo.local_cobranca,
        historico: titulo.historico,
        tipoDC: titulo.tipo_debito_credito
    })

    const { mutate: atualizarTitulo, isLoading } = useMutation({
        mutationKey: "atualizar-titulo-pagar",
        mutationFn: (input: any) => axiosClient.put(`financeiro/contas-pagar/atualizar`, input),
        onError: (error) => {
            console.error(error);
            enqueueSnackbar(`Erro ao atualizar título`, { variant: "error" })
        },
        onSuccess: () => {
            queryClient.invalidateQueries("financeiro/contas-pagar/detalhar-titulo-pagar")
            queryClient.invalidateQueries("table-data-grid-financeiro/contas-pagar/pesquisar-titulos-pagar")
            enqueueSnackbar(`Título atualizado com sucesso!`, { variant: "success" })
            handleClose()
        }
    });

    const handleChangeInputs = (prop: keyof TInternFilters, value: any) => {
        let param = value
        if (prop === 'publico') param = {
            tipo_publico: value.tipo_publico,
            codigo_publico: value.codigo_publico,
            nome_pessoa: value.nome_pessoa
        }
        setInternFilters(prevState => ({ ...prevState, [prop]: param }))
    }

    const handleSubmit = () => {
        const params = {
            id_titulo: codigo,
            tipo_publico: internFilters.publico?.tipo_publico,
            codigo_publico: internFilters.publico?.codigo_publico,
            tipo_documento: internFilters.tipo?.id,
            departamento_id: internFilters.departamento?.id,
            local_cobranca_codigo: internFilters.local_cobranca?.codigo,
            historico: internFilters.historico ?? '',
            tipo_dc: internFilters.tipoDC
        }
        atualizarTitulo(params)
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperComponent={PaperDraggable}
            aria-labelledby="draggable-dialog-title"
            fullWidth
            maxWidth={"md"}
            scroll="body"
        >
            <DialogTitle style={{ cursor: 'move', paddingBottom: 0 }} id="draggable-dialog-title">Editar título</DialogTitle>

            <DialogContent>
                <Grid container spacing={2} pt={3}>
                    <Grid size={{xs: 6}}>
                        <SelectPublico
                            value={internFilters.publico as any}
                            onChange={(valor) => handleChangeInputs("publico", valor)}
                        />
                    </Grid>

                    <Grid size={{xs: 6}}>
                        <SelectTipoDocPagar
                            required
                            value={internFilters.tipo as any}
                            onChange={(valor) => handleChangeInputs("tipo", valor)}
                        />
                    </Grid>

                    <Grid size={{xs: 6}}>
                        <FormControl fullWidth>
                            <InputLabel id="select-dc">Tipo D/C</InputLabel>
                            <Select
                                labelId="select-dc"
                                value={internFilters.tipoDC}
                                label="Tipo D/C"
                                onChange={(e) => handleChangeInputs("tipoDC", e.target.value)}
                            >
                                <MenuItem value="D">Débito</MenuItem>
                                <MenuItem value="C">Crédito</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid size={{xs: 6}}>
                        <SelectDepartamentoFinanceiro
                            value={internFilters.departamento}
                            onChange={(valor) => handleChangeInputs("departamento", valor)}
                        />
                    </Grid>

                    <Grid size={{xs: 6}}>
                        <SelectLocaisCobranca
                            value={internFilters.local_cobranca}
                            onChange={(valor) => {
                                handleChangeInputs("local_cobranca", valor);
                            }}
                        />
                    </Grid>

                    <Grid size={{xs: 6}}>
                        <TextField
                            label="Histórico"
                            fullWidth
                            value={internFilters.historico}
                            onChange={(e) => {
                                handleChangeInputs("historico", e.target.value.trimStart());
                            }}
                            inputProps={{ maxLength: 30 }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancelar</Button>
                <Button disabled={isLoading} variant="contained" onClick={() => handleSubmit()}>
                    {
                        (isLoading) &&
                        <CircularProgress size={20} sx={{ marginRight: 1 }} />
                    }
                    Editar
                </Button>
            </DialogActions>
        </Dialog>
    )
}

interface DialogEditProps {
    open: boolean;
    handleClose: () => void;
    titulo: IDetalharTituloPagar
    codigo: string
}

type TInternFilters = {
    publico: Publico | null,
    tipo: { id: string, nome: string } | null,
    departamento: IDataDepartamentosFinanceiros | null,
    local_cobranca: TLocalCobranca | null,
    historico: string,
    tipoDC: string
}